import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MainLayout from './components/layout/MainLayout';
import { DealerRoutes, AdminRoutes } from './routes';
import MuiThemeProvider from './providers/MuiThemeProvider';
import { LightBox } from './components/common/LightBox/LightBox';

// Lazy loaded Pages
import LoginPage from './pages/LoginPage/Login.lazy';
import PageNotFound from './pages/PageNotFound/PageNotFound.lazy';
import UserEmailConfirmationPage from './pages/UserEmailConfirmationPage/UserEmailConfirmationPage.lazy';
import PasswordResetPage from './pages/PasswordResetPage/PasswordResetPage.lazy';
import RegistrationPage from './pages/RegistrationPage/RegistrationPage.lazy';
import ThankYouPage from './pages/ThankYouPage/ThankYouPage.lazy';
import CartConfiguratorPage from './pages/CartConfiguratorPage/CartConfiguratorPage.lazy';
import DealerLocatorPage from "./pages/DealerLocatorPage/DealerLocatorPage.lazy";

// Providers | Auth | Guards
import RequireAuth from './guards/RequireAuth';
import { AuthProvider } from './context/AuthContext';
import RequireAdminAuth from './guards/RequireAdminAuth';
import PersistLogin from './guards/PersistLogin';
import { CartOrderProvider } from './context/CartOrderContext';
import { LightBoxProvider } from './context/LightBoxContext';
import { CurrencyConverterProvider } from './context/CurrencyConverterContext';
import ErrorBoundary from './ErrorBoundary';
import { APIProvider as GoogleMapsAPIProvider } from '@vis.gl/react-google-maps';
import { GoogleMapsLibraryProvider } from "./context/GoogleMapsLibraryContext";

function App() {
  return (
    <div className="App">
      <GoogleMapsAPIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? ''}>
        <GoogleMapsLibraryProvider>
          <MuiThemeProvider>
            <AuthProvider>
              <CartOrderProvider>
                <LightBoxProvider>
                  <CurrencyConverterProvider>
                    <RouterProvider router={router} />
                  </CurrencyConverterProvider>
                  <LightBox />
                </LightBoxProvider>
              </CartOrderProvider>
            </AuthProvider>
          </MuiThemeProvider>
        </GoogleMapsLibraryProvider>
      </GoogleMapsAPIProvider>
      <ToastContainer
        position="bottom-center"
        autoClose={10000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover />
    </div >
  );
}


const router = 
  createBrowserRouter(
    createRoutesFromElements(
      <>
        {process.env.REACT_APP_BASENAME === '/build-your-own' &&
          <>
            <Route path="*" element={<ErrorBoundary><CartConfiguratorPage /></ErrorBoundary>} />
            <Route index path="/" element={<ErrorBoundary><CartConfiguratorPage /></ErrorBoundary>} />
          </>
        }
        {process.env.REACT_APP_BASENAME === '/dealer-locator' &&
          <>
            <Route path="*" element={<ErrorBoundary><DealerLocatorPage /></ErrorBoundary>} />
            <Route index path="/" element={<ErrorBoundary><DealerLocatorPage /></ErrorBoundary>} />
          </>
        }
        {process.env.REACT_APP_BASENAME === '/dealer-portal-dev' &&
          <>
            <Route path="/build-your-own/*" element={<ErrorBoundary><CartConfiguratorPage /></ErrorBoundary>} />
            <Route index path="/dealer-locator" element={<ErrorBoundary><DealerLocatorPage /></ErrorBoundary>} />
          </>
        }
        {(process.env.REACT_APP_BASENAME !== '/build-your-own' && process.env.REACT_APP_BASENAME !== '/dealer-locator') &&
          <>
            <Route path="/login" element={<ErrorBoundary><LoginPage /></ErrorBoundary>} />
            <Route path="/404" element={<PageNotFound />} />
            <Route path="/email-confirmation" element={<ErrorBoundary><UserEmailConfirmationPage /></ErrorBoundary>} />
            <Route path="/password-reset" element={<ErrorBoundary><PasswordResetPage /></ErrorBoundary>} />
            <Route path="/warranty-registration" element={<ErrorBoundary><RegistrationPage /></ErrorBoundary>} />
            <Route path="/thank-you" element={<ErrorBoundary><ThankYouPage /></ErrorBoundary>} />
            <Route element={<ErrorBoundary><PersistLogin /></ErrorBoundary>}>
              <Route element={<RequireAuth />}>
                <Route path="/" element={<ErrorBoundary><MainLayout /></ErrorBoundary>}>
                  {DealerRoutes}
                  <Route element={<RequireAdminAuth />}>
                    {AdminRoutes}
                  </Route>
                </Route>
              </Route>
            </Route>
          </>
        }
      </>
    ),
    { basename: process.env.REACT_APP_BASENAME }
  )

export default App;
